import axios from 'axios';

const getApiBaseUrl = () => {
  // Use environment variable if defined, otherwise default to localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
};

export const API_BASE_URL = getApiBaseUrl();
export const API_VERSION = 'api/v1';
export const API_TIMEOUT_MS = 10000;

export const apiClient = axios.create({
  timeout: API_TIMEOUT_MS
});

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const apiRequest = async (config, { retry = 1, retryDelayMs = 500 } = {}) => {
  try {
    return await apiClient.request(config);
  } catch (error) {
    if (retry > 0) {
      await wait(retryDelayMs);
      return apiClient.request(config);
    }
    throw error;
  }
};

// Helper function to build full API URL
export const getApiUrl = (endpoint) => {
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

  // Remove 'api/' prefix from endpoint if present (we'll add the versioned one)
  const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');

  // Ensure API_BASE_URL doesn't end with a slash
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  return `${baseUrl}/${API_VERSION}/${endpointWithoutApi}`;
};

// Generate authentication headers using HMAC-SHA256
export const getAuthHeaders = async () => {
  const apiKey = process.env.REACT_APP_API_SECRET_KEY;

  if (!apiKey) {
    console.error('REACT_APP_API_SECRET_KEY not configured');
    throw new Error('API authentication not configured');
  }

  const timestamp = Date.now().toString();

  // Use Web Crypto API for HMAC-SHA256 signing
  const encoder = new TextEncoder();
  const keyData = encoder.encode(apiKey);
  const messageData = encoder.encode(timestamp);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signatureBuffer = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
  const signatureArray = Array.from(new Uint8Array(signatureBuffer));
  const signature = signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return {
    'X-API-Timestamp': timestamp,
    'X-API-Signature': signature
  };
};

